import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useSelector } from "react-redux";
import MenuItemDetailModal from "@/components/menu/MenuItemDetailModal";
import { useDetailModalOpen } from "@/context/detail-modal-context";
import { usePublicTenant } from "@/context/PublicTenantProvider";
import { fetchMenuCatalogItems } from "@/hooks/use-menu-items";
import type { MenuItemInterface } from "@/models/menu-item";
import store, { type RootState } from "@/store/store";
import { isIndividuallySellable } from "@/utils/item-sellability";

export type OpenMenuItemDetailsOptions = {
  /** When true, skip cart-lock guard (tests / rare admin flows). Default false. */
  force?: boolean;
  /**
   * Open intent. Defaults to `'add'` when omitted (browsing / existing callers).
   * `'edit'` stores cart-line identity for quantity-only Edit in the shared modal.
   */
  mode?: "add" | "edit";
  /** Required when `mode === 'edit'`. Cart line `client_item_id`. */
  client_item_id?: string;
};

type MenuItemDetailsContextValue = {
  /** Opens the shared Dishes item-details sheet for a catalog menu item. */
  openMenuItemDetails: (
    menuItemId: string,
    options?: OpenMenuItemDetailsOptions
  ) => Promise<boolean>;
  closeMenuItemDetails: () => void;
  isOpen: boolean;
  selectedMenuItemId: string | null;
  /** `'edit'` only when opened with a valid edit identity; otherwise `'add'`. */
  detailMode: "add" | "edit";
  /** Bound cart line id when `detailMode === 'edit'`; otherwise null. */
  editClientItemId: string | null;
};

const MenuItemDetailsContext = createContext<MenuItemDetailsContextValue | null>(
  null
);

/**
 * Single source of truth for “open item details → add to cart” across Dishes,
 * legacy Home, and storefront Homepage product CTAs. Mount once under
 * {@link DetailModalProvider}.
 */
export function MenuItemDetailsProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const [selectedForDetailId, setSelectedForDetailId] = useState<string | null>(
    null
  );
  const [detailMode, setDetailMode] = useState<"add" | "edit">("add");
  const [editClientItemId, setEditClientItemId] = useState<string | null>(null);
  const { setDetailModalOpen } = useDetailModalOpen();
  const { slug: tenantSlug } = usePublicTenant();
  const menuItems = useSelector(
    (state: RootState) => state.menuItems?.list ?? []
  );

  const selectedItem = useMemo(
    () =>
      selectedForDetailId
        ? (menuItems.find(
            (el) => el.menu_item_id === selectedForDetailId
          ) as MenuItemInterface | undefined)
        : undefined,
    [selectedForDetailId, menuItems]
  );

  const closeMenuItemDetails = useCallback(() => {
    setSelectedForDetailId(null);
    setDetailMode("add");
    setEditClientItemId(null);
    setDetailModalOpen(false);
  }, [setDetailModalOpen]);

  const openMenuItemDetails = useCallback(
    async (
      menuItemId: string,
      options?: OpenMenuItemDetailsOptions
    ): Promise<boolean> => {
      const id = String(menuItemId ?? "").trim();
      if (!id) return false;

      // Cart lock must not block opening item details (browsing remains allowed).
      // Add/quantity paths surface CartLockRecovery instead of failing silently.

      let list = store.getState().menuItems?.list ?? [];
      let found = list.find((el) => el.menu_item_id === id);
      if (!found) {
        await fetchMenuCatalogItems(store.dispatch, {
          slug: (tenantSlug ?? "").trim(),
        });
        list = store.getState().menuItems?.list ?? [];
        found = list.find((el) => el.menu_item_id === id);
      }
      if (!found) return false;

      const wantsEdit = options?.mode === "edit";
      const editId = wantsEdit
        ? String(options?.client_item_id ?? "").trim()
        : "";
      // NOT_SOLD_INDIVIDUALLY items are never openable as a standalone
      // add-to-cart sheet (direct `?menu_item_id=` links included). Editing an
      // existing cart line stays allowed, and modifier/combo selection never
      // routes through here — it renders from the parent item's own data.
      if (!(wantsEdit && editId) && !isIndividuallySellable(found)) return false;
      // Edit identity is stored for later slices; invalid edit falls back to Add storage.
      if (wantsEdit && editId) {
        setDetailMode("edit");
        setEditClientItemId(editId);
      } else {
        setDetailMode("add");
        setEditClientItemId(null);
      }

      setSelectedForDetailId(id);
      setDetailModalOpen(true);
      return true;
    },
    [setDetailModalOpen, tenantSlug]
  );

  const value = useMemo<MenuItemDetailsContextValue>(
    () => ({
      openMenuItemDetails,
      closeMenuItemDetails,
      isOpen: selectedForDetailId !== null,
      selectedMenuItemId: selectedForDetailId,
      detailMode,
      editClientItemId,
    }),
    [
      openMenuItemDetails,
      closeMenuItemDetails,
      selectedForDetailId,
      detailMode,
      editClientItemId,
    ]
  );

  return (
    <MenuItemDetailsContext.Provider value={value}>
      {children}
      {selectedItem ? (
        <MenuItemDetailModal
          item={selectedItem}
          isOpen={selectedForDetailId !== null}
          onClose={closeMenuItemDetails}
          editClientItemId={editClientItemId}
        />
      ) : null}
    </MenuItemDetailsContext.Provider>
  );
}

/**
 * Shared quick-add / item-details opener used by Dishes cards and Homepage carousel.
 */
export function useOpenMenuItemDetails(): MenuItemDetailsContextValue {
  const ctx = useContext(MenuItemDetailsContext);
  if (!ctx) {
    throw new Error(
      "useOpenMenuItemDetails must be used within MenuItemDetailsProvider"
    );
  }
  return ctx;
}
